import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PricingInquiryModal from './PricingInquiryModal';

function AboutViewTags({ activeView, onViewChange }) {
  return (
    <div className="about-view-tags">
      <button
        type="button"
        className={`section-tag ${activeView === 'about' ? 'section-tag--active' : 'section-tag--inactive'}`}
        onClick={() => onViewChange('about')}
      >
        About
      </button>
      <button
        type="button"
        className={`section-tag ${activeView === 'pricing' ? 'section-tag--active' : 'section-tag--inactive'}`}
        onClick={() => onViewChange('pricing')}
      >
        Pricing
      </button>
    </div>
  );
}

function About() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeView = searchParams.get('view') === 'pricing' ? 'pricing' : 'about';
  const [pricingInquiry, setPricingInquiry] = useState(null);

  const scrollToContent = () => {
    const mainContent = document.querySelector('.main-content');
    if (mainContent) {
      mainContent.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const handleViewChange = (view) => {
    if (view === 'pricing') {
      setSearchParams({ view: 'pricing' });
    } else {
      setSearchParams({});
    }
    setTimeout(scrollToContent, 100);
  };

  return (
    <div className="about-page">
      {activeView === 'about' ? (
        <>
          <section className="about-intro-section" id="about-content">
            <div className="section-header">
              <AboutViewTags activeView={activeView} onViewChange={handleViewChange} />
              <h1>We Are Your Trust Guides</h1>
            </div>
            <div className="about-content">
              <p className="about-lead">Empathos was born out of experience.</p>
              <p>
                We know the pressure leaders carry. The constant responsibility to deliver results, sustain performance, manage people, and hold teams together through growth, uncertainty, and change. We also know that low trust carries a high cost for revenue, retention, and relationships.
              </p>
              <p>
                We&rsquo;ve experienced the frustration of low-trust workplaces firsthand. Communication weakens, alignment slips, decision-making slows, and good people disengage. Leaders are often left carrying the burden of maintaining culture, morale, and performance without a clear way to understand where trust is breaking down or how to rebuild it.
              </p>

              <p className="about-pullquote">But we also know the other side.</p>

              <p>
                We know what high trust feels like. We know the energy of teams where people communicate openly, take ownership, support one another, and perform at their best together. We&rsquo;ve seen how high-trust environments strengthen culture, improve retention, deepen relationships, and create healthier, more sustainable performance over time. Because we spend so much of our lives at work, we believe the quality of trust within teams shapes far more than workplace outcomes alone.
              </p>

              <h2 className="about-do-heading">That is why we created Trust Intelligence<sup>&trade;</sup></h2>

              <p>
                We wanted to help leaders make the invisible visible and the unmeasured measurable. Through our proprietary Trust Intelligence<sup>&trade;</sup> platform, we help organisations uncover hidden trust gaps, strengthen practical trust-building skills, and build stronger relationships between leaders and teams over time.
              </p>
              <p>
                We believe trust should not be left to instinct alone. It can be measured, nurtured, and strengthened through intentional leadership practice.
              </p>

              <div className="about-values">
                <h3 className="about-values-heading">Our Values</h3>
                <div className="about-values-grid">
                  <div className="about-values-card">
                    <h4>Empathy</h4>
                    <p>We seek to understand before we advise.</p>
                  </div>
                  <div className="about-values-card">
                    <h4>Trust</h4>
                    <p>High trust is in our DNA.</p>
                  </div>
                  <div className="about-values-card">
                    <h4>Serenity</h4>
                    <p>We bring calm to complexity.</p>
                  </div>
                </div>
              </div>

              <p className="about-tagline">
                Measure. Grow. Nurture your Trust Intelligence<sup>&trade;</sup> with Empathos.
              </p>
            </div>
          </section>

          <section className="team-section">
            <div className="section-header">
              <span className="section-tag">Chief Trust Officers</span>
              <h2>Meet Our Team</h2>
            </div>

            <div className="team-grid">
              <div className="team-member">
                <div className="member-image member-image--mark">
                  <img src="/Incubator_Headshots_26thFeb26-61.jpg" alt="Mark Notaras" />
                </div>
                <h3>Mark Panayiotis Notaras</h3>
                <h4>Cofounder &amp; Chief Executive Officer</h4>
                <p>
                  Mark is a founder of Trust Intelligence<sup>&trade;</sup> and co-developed his high-trust leadership approach through his work with the Timor-Leste Food Lab. With over a decade of executive leadership experience across Australia and the Asia-Pacific, his background spans government, international development, and the private sector.
                </p>
                <p>
                  Frustrated by how low trust quietly erodes team performance, Mark now supports leaders to build high-trust, high-performing teams. In his spare time, he takes a strong interest in applying successful sporting team cultures to workplace leadership.
                </p>
              </div>

              <div className="team-member">
                <div className="member-image">
                  <img src="/joe_pic.jpeg" alt="Joseph Freach" />
                </div>
                <h3>Joseph Freach</h3>
                <h4>Cofounder &amp; Chief Data Officer</h4>
                <p>
                  Joe leads data, insights, and platform delivery at Empathos. Working closely with leaders, he interprets Trust Intelligence<sup>&trade;</sup> results and translates them into clear, practical action.
                </p>
                <p>
                  With a background in analytics and leadership development, Joe works across Australia and the Asia-Pacific to help leaders strengthen trust, improve performance, and lead with confidence. In his spare time, he enjoys bespoke carpentry and exploring Tasmania&rsquo;s natural landscapes.
                </p>
              </div>

              <div className="team-member">
                <div className="member-image">
                  <img src="/mz_pic.jpg" alt="Michael Zlatarich" />
                </div>
                <h3>Michael Zlatarich</h3>
                <h4>Cofounder &amp; CTO</h4>
                <p>
                  Mike is the technical architect of the Trust Intelligence<sup>&trade;</sup> platform at Empathos (and also built this site). He has over 15 years of experience across business, government, and non-profits, designing analytics and data management solutions. He&rsquo;s also an avid rock climber (countless &ldquo;trust&rdquo; falls), traveler, and tinkerer. He hopes you enjoy using Empathos.
                </p>
              </div>
            </div>
          </section>
        </>
      ) : (
        <section className="about-intro-section about-pricing-section">
          <div className="section-header">
            <AboutViewTags activeView={activeView} onViewChange={handleViewChange} />
            <h1>Pricing</h1>
            <p className="pricing-subtitle">
              Find the Trust Intelligence<sup>&trade;</sup> pathway designed for you.
            </p>
          </div>
          <div className="about-content pricing-intro">
            <p className="pricing-intro-lead">
              Whether you&rsquo;re looking to measure trust, accelerate growth, transform your leadership, or strengthen trust across an entire organisation, there&rsquo;s a Trust Intelligence<sup>&trade;</sup> pathway designed for you.
            </p>
            <p>
              Each option combines <strong>measurement</strong>, <strong>insight</strong>, and <strong>action</strong> to help you build stronger, higher-trust teams.
            </p>
          </div>
          <div className="pricing-sheet-layout">
            <img
              src="/trust-intelligence-pricing.png"
              alt="Trust Intelligence pricing for Measure, Grow, Transform, and Enterprise"
              className="pricing-image"
            />
            <div className="pricing-cta-row">
              <button
                type="button"
                className="pricing-cta-pill pricing-cta-pill--measure"
                onClick={() => setPricingInquiry('Measure')}
              >
                Start Measuring
              </button>
              <button
                type="button"
                className="pricing-cta-pill pricing-cta-pill--grow"
                onClick={() => setPricingInquiry('Grow')}
              >
                Start Growing
              </button>
              <button
                type="button"
                className="pricing-cta-pill pricing-cta-pill--transform"
                onClick={() => setPricingInquiry('Transform')}
              >
                Start Transforming
              </button>
              <button
                type="button"
                className="pricing-cta-pill pricing-cta-pill--scale"
                onClick={() => setPricingInquiry('Enterprise')}
              >
                Start Scaling
              </button>
            </div>
          </div>
          <p className="pricing-readiness-cta">
            Select the option that best fits your goals and we&rsquo;ll start with a <strong>Leadership Readiness Conversation</strong>.
          </p>
        </section>
      )}

      <footer className="site-footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-section">
              <h4>Navigation</h4>
              <nav className="footer-nav">
                <Link to="/" onClick={() => setTimeout(() => {
                  const mainContent = document.querySelector('.main-content');
                  if (mainContent) {
                    mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100)}>Home</Link>
                <Link to="/about" onClick={() => setTimeout(() => {
                  const mainContent = document.querySelector('.main-content');
                  if (mainContent) {
                    mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100)}>About</Link>
                <Link to="/contact" onClick={() => setTimeout(() => {
                  const mainContent = document.querySelector('.main-content');
                  if (mainContent) {
                    mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100)}>Contact</Link>
                <Link to="/login" onClick={() => setTimeout(() => {
                  const mainContent = document.querySelector('.main-content');
                  if (mainContent) {
                    mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100)}>Login</Link>
              </nav>
            </div>

            <div className="footer-section">
              <h4>Connect</h4>
              <div className="social-links">
                <a href="https://www.linkedin.com/company/empathostrustintelligence/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="currentColor"/>
                  </svg>
                  LinkedIn
                </a>
                <a href="mailto:team@empathos.com.au" target="_blank" rel="noopener noreferrer" aria-label="Email">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" fill="none"/>
                    <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" fill="none"/>
                  </svg>
                  Email
                </a>
              </div>
            </div>

            <div className="footer-section">
              <h4>Trust Intelligence<sup>&trade;</sup></h4>
              <div className="definition">
                <div className="definition-term">
                  /trʌst ɪnˈtelɪdʒəns/
                </div>
                <div className="definition-part">
                  <em>noun</em>
                </div>
                <div className="definition-meaning">
                  The measurable capacity to understand and strengthen trust between leaders and their teams.
                </div>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="copyright">
              <p><em>We acknowledge the lands of the Bidjigal People of the Eora Nation upon whose land we work.</em></p>
              <p>&copy; {new Date().getFullYear()} Empathos. All rights reserved.</p>
            </div>
            <div className="footer-logo">
              <a href="/" aria-label="Return to home">
                <img src="/empy_svg_test.svg" alt="Empathos" className="footer-logo-svg" />
              </a>
            </div>
          </div>
        </div>
      </footer>

      {pricingInquiry && (
        <PricingInquiryModal
          option={pricingInquiry}
          onClose={() => setPricingInquiry(null)}
        />
      )}
    </div>
  );
}

export default About;
